import { DndContext, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext } from '@dnd-kit/sortable';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import useSet from 'react-use/lib/useSet';
import { Text } from '../../../../../../../../CommonComponents/OPL/Text/Text';
import { Button } from '../../../../../../../../CommonComponents/OPL/Button/Button';
import { Checkbox } from '../../../../../../../../CommonComponents/OPL/Checkbox/Checkbox';
import { ListDivider } from '../../../../../../../../CommonComponents/OPL/List/ListDivider';
import { Box } from '../../../../../../../../CommonComponents/OPL/Box/Box';
import { toast } from '../../../../../../../../CommonComponents/OPL/Toast/Toast';
import { MESSAGES } from '../../../../../../../../CommonConstants';
import { usePlans } from '../../../../../../hooks/usePlans';
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from '../../../../../../../../CommonComponents/OPL/Popover/Popover';
import { isAdviserArea as selectIsAdviserArea } from '../../../../../../../../CommonSelectors/CommonAreaSelector';
import { PLANS_MAX_COUNT, PLANS_MIN_COUNT } from '../../RetirementSideBySide.meta';
import { useComparePlansDisplayOrderQuery } from '../../api/useComparePlansDisplayOrderQuery';
import { useSaveComparePlansDisplayOrderMutation } from '../../api/useSaveComparePlansDisplayOrderMutation';
import {
  arePlanIdOrdersEqual,
  mapPlansByOrderedIds,
  resolveOrderedPlanIds,
} from '../../utils/orderPlanIdsByDisplayOrder';
import { SortablePlanSelectorItem } from '../SortablePlanSelectorItem/SortablePlanSelectorItem';

interface Props {
  clientId: string;
  defaultSelected?: string[];
  anchorEl?: React.ReactElement;
  onApply(selectedSet: string[]): void;
}

export const PlansSelector = ({ clientId, defaultSelected, onApply, anchorEl }: Props) => {
  const [selectedSet, { has, toggle, reset }] = useSet(new Set(defaultSelected || []));
  const isAdviserArea = useSelector(selectIsAdviserArea);

  const plans = usePlans({ clientId });
  const allPlanIds = useMemo(() => plans.map(({ planId }) => planId), [plans]);
  const hasUserReorderedRef = useRef(false);

  const { data: displayOrder, isSuccess: isDisplayOrderSuccess } = useComparePlansDisplayOrderQuery(
    {
      clientId,
      enabled: allPlanIds.length >= PLANS_MIN_COUNT,
    },
  );

  const { mutate: saveDisplayOrder } = useSaveComparePlansDisplayOrderMutation(clientId);

  const [orderedPlanIds, setOrderedPlanIds] = useState<string[]>(allPlanIds);

  useEffect(() => {
    if (!isDisplayOrderSuccess) {
      return;
    }

    setOrderedPlanIds(prev => {
      const next = resolveOrderedPlanIds(
        allPlanIds,
        displayOrder,
        prev,
        hasUserReorderedRef.current,
      );

      return arePlanIdOrdersEqual(prev, next) ? prev : next;
    });
  }, [allPlanIds, displayOrder, isDisplayOrderSuccess]);

  const orderedPlans = useMemo(() => mapPlansByOrderedIds(plans, orderedPlanIds), [
    orderedPlanIds,
    plans,
  ]);

  const canReset = !defaultSelected || defaultSelected.length === 0;
  const canReorder =
    isAdviserArea && isDisplayOrderSuccess && orderedPlanIds.length >= PLANS_MIN_COUNT;

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id || !canReorder) {
      return;
    }

    const oldIndex = orderedPlanIds.indexOf(String(active.id));
    const newIndex = orderedPlanIds.indexOf(String(over.id));

    if (oldIndex < 0 || newIndex < 0) {
      return;
    }

    hasUserReorderedRef.current = true;
    setOrderedPlanIds(arrayMove(orderedPlanIds, oldIndex, newIndex));
  };

  return (
    <Popover>
      <PopoverTrigger>
        {anchorEl || (
          <Button variant="secondary" rightIcon="fas fa-caret-down">
            <Text mr="16">{selectedSet.size > 0 ? selectedSet.size : 'No'} Plans Selected</Text>
          </Button>
        )}
      </PopoverTrigger>

      <PopoverContent width={280}>
        {canReorder ? (
          <DndContext onDragEnd={handleDragEnd}>
            <SortableContext items={orderedPlanIds}>
              {orderedPlans.map(({ planId, name }) => (
                <SortablePlanSelectorItem
                  key={planId}
                  planId={planId}
                  name={name}
                  checked={has(planId)}
                  disabled={!has(planId) && selectedSet.size === PLANS_MAX_COUNT}
                  onToggle={() => toggle(planId)}
                />
              ))}
            </SortableContext>
          </DndContext>
        ) : (
          <Box mx="4" my="4">
            {orderedPlans.map(({ planId, name }) => (
              <Checkbox
                key={planId}
                checked={has(planId)}
                onChange={() => toggle(planId)}
                disabled={!has(planId) && selectedSet.size === PLANS_MAX_COUNT}
                isOverflowWrap
                my="8"
              >
                {name}
              </Checkbox>
            ))}
          </Box>
        )}

        <ListDivider />

        <Box display="flex" justifyContent={canReset ? 'space-between' : 'flex-end'} px="16" py="8">
          {canReset && (
            <Button variant="subtleInactive" onClick={reset}>
              Reset
            </Button>
          )}

          <PopoverClose>
            <Button
              onClick={() => {
                if (canReorder) {
                  saveDisplayOrder(orderedPlanIds, {
                    onSuccess: () => {
                      hasUserReorderedRef.current = false;
                    },
                    onError: () => {
                      toast.error(MESSAGES.GENERIC_ERROR);
                    },
                  });
                }

                const planIds = orderedPlanIds.filter(planId => has(planId));

                onApply(planIds);
              }}
              disabled={selectedSet.size < PLANS_MIN_COUNT}
            >
              Apply
            </Button>
          </PopoverClose>
        </Box>
      </PopoverContent>
    </Popover>
  );
};
